/**
 * i18n shim - English only.
 *
 * Multi-language support was removed on 2026-04-19 (users target English;
 * the overhead of maintaining 9 extra locales + dedicated SEO pages wasn't
 * paying off). This file is intentionally reduced to a thin English-only
 * translator so existing `t()` / `useTranslation()` call sites keep working
 * without a codebase-wide rewrite. Safe to delete the whole i18n plumbing
 * once every consumer has been inlined.
 */

import enStrings from '@/locales/en.json';
import type { Language } from '@/types';

export type { Language };

export const SUPPORTED_LANGUAGES: {
  code: Language;
  name: string;
  nativeName: string;
  script: string;
}[] = [
  { code: 'en', name: 'English', nativeName: 'English', script: 'Latin' },
];

function getNestedValue(obj: Record<string, unknown>, path: string): string | undefined {
  const keys = path.split('.');
  let current: unknown = obj;
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : undefined;
}

export function createTranslator(_lang: Language = 'en', strings?: typeof enStrings) {
  const langStrings = strings || enStrings;
  return function t(key: string, params?: Record<string, string | number>): string {
    let value = getNestedValue(langStrings as unknown as Record<string, unknown>, key);
    if (value === undefined) {
      console.warn(`Missing translation: ${key}`);
      return key;
    }
    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value!.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(paramValue));
      });
    }
    return value;
  };
}

export const t = createTranslator('en');

/** Kept for backwards-compat with any caller that dynamically loads. Always returns English. */
export async function loadTranslations(_lang: Language): Promise<typeof enStrings> {
  return enStrings;
}

/** Kept for backwards-compat. Always returns English. */
export function getTranslationsSync(_lang: Language): typeof enStrings {
  return enStrings;
}

export function isValidLanguage(code: string): code is Language {
  return code === 'en';
}

/** Kept for backwards-compat with any legacy caller. Always returns 'en'. */
export function getLanguageFromCookie(): Language {
  return 'en';
}

/** No-op; we no longer persist a language preference. */
export function setLanguageCookie(_lang: Language): void {
  // no-op
}

/** Kept for backwards-compat. Always returns 'en'. */
export function getLanguageFromHeaders(_acceptLanguage?: string | null): Language {
  return 'en';
}
